import { resolveContentLink } from "@/lib/contentLink";
import { useState, useEffect } from "react";
import { PageMeta } from "@/components/PageMeta";
import { SeoHead } from "@/components/SeoHead";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Loader2, FileText } from "lucide-react";
import daniqueAvatar from "@/assets/authors/danique-geskus.webp";
import lotteAvatar from "@/assets/authors/lotte-heijnen.png";

const AUTHOR_AVATARS: Record<string, string> = {
  "danique-geskus": daniqueAvatar,
  "lotte-heijnen": lotteAvatar,
};
import { PageContainer } from "@/components/layouts/MainLayout";
import { cn } from "@/lib/utils";
import { T } from "@/components/editable/T";
import NotFound from "@/pages/NotFound";
import { NewsletterBanner } from "@/components/blog/NewsletterBanner";
import { getPayTransparencyAssessmentPath } from "@/lib/routeUtils";
import { migratedCanonicalSlug } from "@/lib/localizedSlugMigration";

interface BlogArticle {
  id: string;
  slug: string;
  category: string;
  author: string;
  date: string;
  read_time: string;
  featured: boolean;
  hero_image_url: string | null;
  published: boolean;
}

interface BlogSection {
  id: string;
  article_id: string;
  section_key: string;
  sort_order: number;
  has_image: boolean;
  image_url: string | null;
  include_in_toc: boolean;
}

interface TranslationRow {
  key: string;
  nl: string;
  en: string | null;
  de: string | null;
}

const categoryConfig = {
  blog: { labelKey: "categories.blog", color: "#F3EDFD", textColor: "#9055EE" },
  nieuws: { labelKey: "categories.news", color: "#FDF2ED", textColor: "#EA580C" },
  releases: { labelKey: "categories.releases", color: "#FFFBEB", textColor: "#CA8A04" },
};

const PAGE = "blog";

const BlogDetail = () => {
  const { t, i18n } = useTranslation();
  const { lang, '*': splat } = useParams<{ lang: string; '*': string }>();
  const normalizedSplat = splat?.replace(/^\/+|\/+$/g, "") ?? "";
  const pathSegments = normalizedSplat.split("/").filter(Boolean);
  const urlSlug = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : undefined;
  const currentArticlePath = normalizedSplat ? `/${normalizedSplat}` : null;
  const currentArticlePathCandidates = currentArticlePath
    ? [currentArticlePath, `${currentArticlePath}/`]
    : [];
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const currentLang = (i18n.language || lang || "nl") as "nl" | "en" | "de";

  // Resolve slug: if the URL slug doesn't match an article directly (e.g. DE slug),
  // look up the canonical NL slug via website_urls
  const { data: resolvedSlug } = useQuery({
    queryKey: ["blog-slug-resolve", lang, normalizedSplat],
    queryFn: async () => {
      if (!urlSlug) return urlSlug;
      const migratedSlug = migratedCanonicalSlug(`/${lang}/${normalizedSplat}/`);
      if (migratedSlug) return migratedSlug;
      if (urlSlug.startsWith("job-demands-resources-model")) {
        return "job-demands-resources-model";
      }
      // First try direct match
      const { data: direct } = await supabase
        .from("blog_articles")
        .select("slug")
        .eq("slug", urlSlug)
        .maybeSingle();
      if (direct) return direct.slug;
      // Fallback: look up via website_urls path_de or path_en
      const pathField = lang === 'de' ? 'path_de' : lang === 'en' ? 'path_en' : 'path_nl';
      if (currentArticlePathCandidates.length === 0) return urlSlug;
      const { data: urlRecord } = await supabase
        .from("website_urls")
        .select("path_nl")
        .in(pathField, currentArticlePathCandidates)
        .eq("is_active", true)
        .maybeSingle();
      if (urlRecord?.path_nl) {
        const nlPathSegments = urlRecord.path_nl.split('/').filter(Boolean);
        const nlSlug = nlPathSegments.length > 0 ? nlPathSegments[nlPathSegments.length - 1] : undefined;
        return nlSlug || urlSlug;
      }
      return urlSlug;
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!urlSlug,
  });

  const slug = resolvedSlug || urlSlug;

  // Fetch article from database
  const { data: article, isLoading: articleLoading } = useQuery({
    queryKey: ["blog-article", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("blog_articles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data as BlogArticle;
    },
    enabled: !!slug,
  });

  // Fetch article sections
  const { data: sections = [] } = useQuery({
    queryKey: ["blog-sections", article?.id],
    queryFn: async () => {
      if (!article?.id) return [];
      const { data, error } = await supabase
        .from("blog_sections")
        .select("*")
        .eq("article_id", article.id)
        .order("sort_order");
      if (error) throw error;
      return data as BlogSection[];
    },
    enabled: !!article?.id,
  });

  const { data: articleTranslations = [] } = useQuery({
    queryKey: ["blog-article-translations", slug],
    queryFn: async () => {
      if (!slug) return [];
      const { data, error } = await supabase
        .from("translations")
        .select("key, nl, en, de")
        .eq("page", PAGE)
        .like("key", `articles.${slug}%`);

      if (error) throw error;
      return (data ?? []) as TranslationRow[];
    },
    enabled: !!slug,
  });

  // Fetch related articles: the 3 articles published right after the current one
  const { data: relatedArticles = [] } = useQuery({
    queryKey: ["related-articles", slug, article?.date],
    queryFn: async () => {
      if (!article) return [];
      
      // Get articles with an earlier (or same) date, excluding current article
      const { data: olderArticles, error: olderError } = await supabase
        .from("blog_articles")
        .select("*")
        .eq("published", true)
        .neq("slug", slug)
        .lte("date", article.date)
        .order("date", { ascending: false })
        .limit(3);
      
      if (olderError) throw olderError;
      
      // If we have 3, we're done. Otherwise wrap around to the newest articles
      if (olderArticles && olderArticles.length >= 3) {
        return olderArticles as BlogArticle[];
      }
      
      const remaining = 3 - (olderArticles?.length || 0);
      const excludeSlugs = [slug, ...(olderArticles || []).map(a => a.slug)];
      
      const { data: newerArticles, error: newerError } = await supabase
        .from("blog_articles")
        .select("*")
        .eq("published", true)
        .not("slug", "in", `(${excludeSlugs.map(s => `"${s}"`).join(",")})`)
        .order("date", { ascending: false })
        .limit(remaining);
      
      if (newerError) throw newerError;
      
      return [...(olderArticles || []), ...(newerArticles || [])] as BlogArticle[];
    },
    enabled: !!slug && !!article,
  });

  const tocSections = sections.filter((s) => s.include_in_toc);

  const getTranslatedValue = (field: string, fallback = "") => {
    const fullKey = `articles.${slug}.${field}`;
    const i18nKey = `${PAGE}.${fullKey}`;
    const translated = t(i18nKey, { defaultValue: "" }) as string;

    if (translated && translated !== i18nKey) {
      return translated;
    }

    const row = articleTranslations.find((entry) => entry.key === fullKey);
    if (!row) return fallback;

    if (currentLang === "en") return row.en || row.nl || fallback;
    if (currentLang === "de") return row.de || row.nl || fallback;
    return row.nl || fallback;
  };

  const scrollToSection = (sectionKey: string) => {
    const element = document.getElementById(`section-${sectionKey}`);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
    setActiveSection(sectionKey);
  };

  useEffect(() => {
    if (tocSections.length === 0) return;
    const handleScroll = () => {
      for (const section of [...tocSections].reverse()) {
        const element = document.getElementById(`section-${section.section_key}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section.section_key);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tocSections]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString(
      currentLang === "nl" ? "nl-NL" : currentLang === "de" ? "de-DE" : "en-US",
      options
    );
  };

  // Helper: build i18n key for this article
  const ak = (field: string) => `articles.${slug}.${field}`;

  if (articleLoading) {
    return (
      <section className="pt-[140px] pb-20 px-6">
        <PageContainer>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </PageContainer>
      </section>
    );
  }

  if (!article) {
    return <NotFound />;
  }

  const catKey = article.category as keyof typeof categoryConfig;
  const categoryLabel = categoryConfig[catKey]?.labelKey 
    ? t(`blog.${categoryConfig[catKey].labelKey}`)
    : article.category;

  const blogTitle = getTranslatedValue("title", slug || "");
  const customMetaTitle = getTranslatedValue("meta_title", "");
  const customMetaDesc = getTranslatedValue("meta_description", "");
  const metaTitle = customMetaTitle || (blogTitle ? `${blogTitle} | Learned` : "Blog | Learned");
  const metaDesc = customMetaDesc || getTranslatedValue("intro", "").replace(/<[^>]*>/g, '').slice(0, 160);

  return (
    <article>
      <PageMeta title={metaTitle} description={metaDesc || metaTitle} ogImage={article.hero_image_url || undefined} ogType="article" />
      <SeoHead jsonLd={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: blogTitle,
        description: metaDesc || metaTitle,
        image: article.hero_image_url || undefined,
        datePublished: article.date,
        author: {
          "@type": "Person",
          name: article.author,
        },
        publisher: {
          "@type": "Organization",
          name: "Learned",
          logo: { "@type": "ImageObject", url: "https://www.learned.io/favicon.svg" },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://www.learned.io/${lang}/blog/${slug}/`,
        },
      }} />
      {/* Hero Section */}
      <section
        className="pt-[140px] pb-12 px-6 relative"
        style={{ background: 'radial-gradient(circle at center, white 0%, #F3EDFD 70%)' }}
      >
        <PageContainer>
          <div className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="animate-fade-in min-w-0">
              <Link
                to={`/${lang}/blog/`}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                <T page={PAGE} k="back" d="Terug" />
              </Link>

              <div className="mb-6">
                <span
                  className="inline-block px-4 py-1.5 rounded-full text-sm font-medium border"
                  style={{
                    backgroundColor: "#FFFFFF",
                    color: categoryConfig[catKey]?.textColor || "#666",
                    borderColor: categoryConfig[catKey]?.textColor || "#666",
                  }}
                >
                  {categoryLabel}
                </span>
              </div>

              <h1 className="mb-6" style={{ color: "#2A2334", overflowWrap: "break-word", hyphens: "manual" }}>
                {getTranslatedValue("title", article.slug)}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
                <Link
                  to={`/${lang}/author/${article.author.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}/`}
                  className="flex items-center gap-2 hover:text-primary transition-colors group/author"
                >
                  {(() => {
                    const slug = article.author.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
                    const avatar = AUTHOR_AVATARS[slug];
                    return avatar ? (
                      <img src={avatar} alt={article.author} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <User className="w-4 h-4" />
                    );
                  })()}
                  <span className="underline decoration-dotted underline-offset-4 group-hover/author:decoration-solid group-hover/author:text-primary">{article.author}</span>
                </Link>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(article.date)}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Hero Image */}
            {article.hero_image_url && (
              <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}
                >
                  <img
                    src={article.hero_image_url}
                    alt={getTranslatedValue("hero_alt", getTranslatedValue("title", article.slug))}
                    className="w-full h-auto aspect-[16/10] object-cover"
                    style={
                      article.slug === "eerlijk-beoordelen-bedrijfsrisico"
                        ? { objectPosition: "30% 30%" }
                        : undefined
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </PageContainer>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6 relative">
        <PageContainer>
          <div className="flex gap-12">
            <div className="flex-1 min-w-0 lg:pr-8">

              <div className="prose max-w-none [&_a]:no-underline [&_h3]:mt-8 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-[#F8F5FD] [&_blockquote]:rounded-xl [&_blockquote]:px-8 [&_blockquote]:py-6 [&_blockquote]:my-8 [&_blockquote]:not-italic [&_blockquote_p:last-child]:mb-0 [&_blockquote_a]:text-primary [&_blockquote_a]:no-underline [&_.text-center]:text-center [&_p:has(>strong:only-child:has(a))]:text-center">
                {/* Intro */}
                {getTranslatedValue("intro") && (
                  <div
                    className="mb-10 text-muted-foreground text-lead [&_p]:mb-4 [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80 [&_a_strong]:text-primary"
                    dangerouslySetInnerHTML={{ __html: getTranslatedValue("intro") }}
                  />
                )}

                {/* Sections */}
                {sections.map((section) => {
                  const sKey = section.section_key; // e.g. "s1", "s2"
                  const headingKey = ak(`sections.${sKey}.heading`);
                  const contentKey = ak(`sections.${sKey}.content`);
                  const contentText = getTranslatedValue(`sections.${sKey}.content`);
                  const hasContent = !!contentText;

                  const headingText = getTranslatedValue(`sections.${sKey}.heading`);
                  const isBlankHeading = !headingText || headingText.trim() === '';

                  return (
                    <div key={section.id}>
                      <div id={`section-${sKey}`} className="mb-10 scroll-mt-32">
                        {!isBlankHeading && (section.include_in_toc ? (
                          <h2 className="font-semibold mb-4" style={{ color: "#2A2334" }}>
                            {headingText}
                          </h2>
                        ) : (
                          <h3 className="font-semibold mb-3" style={{ color: "#2A2334" }}>
                            {headingText}
                          </h3>
                        ))}
                        {(() => {
                          const imageEl = section.has_image && section.image_url && article.category !== 'releases' ? (
                            section.image_url.includes("checklist") ? (
                              <a
                                href="https://143197515.fs1.hubspotusercontent-eu1.net/hubfs/143197515/Content%20-%20Eguides%20and%20Templates/Templates%20NL/Checklist%20Performance%20management%20software.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block cursor-pointer hover:opacity-90 transition-opacity text-center"
                              >
                                <div className="rounded-2xl overflow-hidden shadow-lg mt-6 mb-6 mx-auto">
                                  <img src={section.image_url} alt={headingText || ""} className="w-full h-auto" />
                                </div>
                              </a>
                            ) : (
                              <div className="rounded-2xl overflow-hidden shadow-lg mt-6 mb-6 mx-auto">
                                <img src={section.image_url} alt={headingText || ""} className="w-full h-auto" />
                              </div>
                            )
                          ) : null;

                          if (!hasContent) return imageEl;

                          // If section has image and content ends with an <em> caption, split it
                          // Move the caption (and the paragraph right before it) below the image
                          if (imageEl && contentText) {
                            const emMatch = contentText.match(/((?:<p>\s*<em>[\s\S]*?<\/em>\s*<\/p>|<em>[\s\S]*?<\/em>)\s*)$/);
                            if (emMatch) {
                              const beforeEm = contentText.slice(0, contentText.lastIndexOf(emMatch[1])).trimEnd();
                              let mainContent = beforeEm;
                              let afterImage = emMatch[1];

                              const isExactSection12 = article.slug === "360-graden-feedback" && sKey === "section12";

                              if (isExactSection12) {
                                // Keep all regular body content above the image and only move the italic explanation below it
                                mainContent = beforeEm;
                                afterImage = emMatch[1];
                              } else if (beforeEm.endsWith('</p>')) {
                                const lastPOpen = beforeEm.lastIndexOf('<p>');
                                if (lastPOpen !== -1) {
                                  const candidate = beforeEm.slice(lastPOpen);
                                  const potentialMain = beforeEm.slice(0, lastPOpen).trim();
                                  const isCta = /[\u{1F4A1}\u{1F3AF}\u{1F449}]|👉|💡|🎯/u.test(candidate);
                                  const isSubstantial = candidate.replace(/<[^>]*>/g, '').length > 200;
                                  if (!/<(?:ul|ol|li|h[1-6])/i.test(candidate) && potentialMain.length > 0 && !isCta && !isSubstantial) {
                                    mainContent = potentialMain;
                                    afterImage = candidate + emMatch[1];
                                  }
                                }
                              }
                              return (
                                <>
                                  {mainContent && (
                                    <div
                                      className="text-muted-foreground text-lead [&_p]:mb-4 [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80 [&_a_strong]:text-primary"
                                      dangerouslySetInnerHTML={{ __html: mainContent }}
                                    />
                                  )}
                                  {imageEl}
                                  <div
                                    className="text-muted-foreground text-lead [&_p]:mb-4 [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80"
                                    dangerouslySetInnerHTML={{ __html: afterImage }}
                                  />
                                </>
                              );
                            }
                          }
                      {/* Inline CTA banner for competentiematrix after specific sections */}
                      {lang === "nl" && article.slug === "competentiematrix-skills-matrix" && (sKey === "s3b" || sKey === "s13") && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                            <h3 className="font-semibold mb-2 text-foreground">
                              Template: Competenti&shy;eset met 38 soft skills
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              + concrete gedragsvoorbeelden voor 5 niveaus 👇
                            </p>
                            <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                              <img
                                src="/images/blog/competentieset-soft-skills-cta.png"
                                alt="Template: Competentieset met 38 soft skills"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                          return (
                            <>
                              <div
                                className="text-muted-foreground text-lead [&_p]:mb-4 [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80 [&_a_strong]:text-primary"
                                dangerouslySetInnerHTML={{ __html: contentText }}
                              />
                              {imageEl}
                            </>
                          );
                        })()}
                      </div>
                      {/* Inline CTA banner for functioneringsgesprek-voorbereiden after section3 */}
                      {lang === "nl" && article.slug === "functioneringsgesprek-voorbereiden" && sKey === "section3" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              Vernieuw je functioneringsgesprekken met deze 25+ voorbeeldvragen.
                            </p>
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <img
                                src="/images/blog/template-evaluatiegesprek-hero.avif"
                                alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Inline CTA banner for 360-graden-feedback after section7 */}
                      {lang === "nl" && article.slug === "360-graden-feedback" && sKey === "section7" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              Vernieuw je functioneringsgesprekken met deze 25+ voorbeeldvragen.
                            </p>
                             <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                               <Button className="rounded-full px-8">Download nu</Button>
                             </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <img
                                src="/images/blog/template-evaluatiegesprek-hero.avif"
                                alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Inline CTA banner for 360-graden-feedback-software after s3 */}
                      {lang === "nl" && article.slug === "360-graden-feedback-software" && sKey === "s3" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              Vernieuw je functioneringsgesprekken met deze 25+ voorbeeldvragen.
                            </p>
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <img
                                src="/images/blog/template-evaluatiegesprek-hero.avif"
                                alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Inline CTA for loopbaangesprekken after voorbeeldvragen */}
                      {lang === "nl" && article.slug === "10-best-practices-voor-effectieve-loopbaangesprekken" && sKey === "voorbeeldvragen" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Template voor een modern functionerings- of evaluatiegesprek
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              25+ voorbeeldvragen voor een modern evaluatiegesprek 👇
                            </p>
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <img
                                src="/images/blog/template-evaluatiegesprek-hero.avif"
                                alt="Template voor een modern evaluatiegesprek"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Inline CTA for functioneringsgesprek-vragen before voorbereiden */}
                      {lang === "nl" && article.slug === "functioneringsgesprek-vragen" && sKey === "10-vragen" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              Vernieuw je functioneringsgesprekken met deze 25+ voorbeeldvragen.
                            </p>
                            <Link to={resolveContentLink(`/${lang}/template/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/template/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                              <img
                                src="/images/blog/template-evaluatiegesprek-hero.avif"
                                alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Inline image for talentmanagement before hoe-bouwen */}
                      {lang === "nl" && article.slug === "6-stappen-om-een-effectief-talentmanagement-programma-te-bouwen" && sKey === "waarom-belangrijk" && (
                        <div className="my-10">
                          <div className="rounded-2xl overflow-hidden">
                            <img
                              src="/images/blog/talentmanagement-cta.png"
                              alt="Learned platform - Summary met prestatiebeoordeling per thema"
                              className="w-full h-auto"
                            />
                          </div>
                          <p className="text-foreground mt-4 mb-2 italic">
                            Learned faciliteert objectieve evaluaties. Door meerdere meetmomenten per jaar te creëren op basis van verschillende thema's – bijvoorbeeld: de fit met je cultuur en het behalen van de doelen, krijg je objectief inzicht in je low- en high performers. <a href="https://app.learned.io/register" target="_blank" rel="noopener noreferrer" className="text-primary underline">Probeer Learned nu 14 dagen gratis!</a>
                          </p>
                          <p className="text-muted-foreground text-sm italic">
                            Toelichting afbeelding: In iedere evaluatie wordt inzichtelijk hoe de medewerker scoort op de verschillende thema's.
                          </p>
                        </div>
                      )}
                      {/* Inline image for talentmanagement after met-learned (end of article) */}
                      {lang === "nl" && article.slug === "6-stappen-om-een-effectief-talentmanagement-programma-te-bouwen" && sKey === "met-learned" && (
                        <div className="my-10">
                          <div className="rounded-2xl overflow-hidden">
                            <img
                              src="/images/blog/talentmanagement-performance.png"
                              alt="Learned platform - Performance rapportage met high en low performers"
                              className="w-full h-auto"
                            />
                          </div>
                          <p className="text-foreground mt-4 italic">
                            Krijg inzicht in de low en high performers van je organisatie en identificeer eenvoudig talent binnen je organisatie.
                          </p>
                        </div>
                      )}
                      {/* Inline visual for check-in gesprekken after learned-platform */}
                      {lang === "nl" && article.slug === "gids-voor-het-voeren-van-effectieve-check-in-gesprekken" && sKey === "learned-platform" && (
                        <div className="my-6">
                          <div className="rounded-2xl overflow-hidden">
                            <img
                              src="/images/blog/check-in-evaluatie.png"
                              alt="Learned platform - Summary met prestatiebeoordeling per thema"
                              className="w-full h-auto"
                            />
                          </div>
                          <p className="text-foreground mt-4 mb-2 italic">
                            Learned faciliteert objectieve evaluaties. Door meerdere meetmomenten per jaar te creëren op basis van verschillende thema's – bijvoorbeeld: de fit met je cultuur en het behalen van de doelen, krijg je objectief inzicht in je low- en high performers. Door deze evaluaties worden de check-in gesprekken gebaseerd op feiten en niet op een onderbuikgevoel van de manager.
                          </p>
                          <p className="text-muted-foreground text-sm italic">
                            Toelichting: In iedere evaluatie wordt inzichtelijk hoe de medewerker scoort op de verschillende thema's.
                          </p>
                        </div>
                      )}
                      {/* Inline CTA for competentieprofiel after section5 */}
                      {lang === "nl" && article.slug === "competentieprofiel" && sKey === "section5" && (
                        <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center my-10 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                          <div className="p-8 md:p-10 flex-1">
                             <h3 className="font-semibold mb-2 text-foreground">
                              Gratis template: Competentieset met 38 soft skills
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              +5 niveaus met concrete gedragsvoorbeelden 👇
                            </p>
                            <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                              <Button className="rounded-full px-8">Download nu</Button>
                            </Link>
                          </div>
                          <div className="md:w-[45%] p-4 md:p-8">
                            <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                              <img
                                src="/images/blog/competentieset-soft-skills-cta.png"
                                alt="Competentieset met 38 soft skills"
                                className="w-full h-auto"
                              />
                            </Link>
                          </div>
                        </div>
                      )}
                      {/* Webinar video embed for loontransparantie after s15 */}
                      {article.slug === "alles-over-de-wet-loontransparantie-en-de-risicos" && sKey === "s15" && (
                        <div className="my-10 rounded-2xl overflow-hidden shadow-lg">
                          <iframe
                            src="https://watch.getcontrast.io/videos/67f52840-c148-4651-98c6-f73c75d1aaab"
                            title="Webinar Loontransparantie"
                            className="w-full aspect-video"
                            allow="fullscreen; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block w-80 shrink-0">
              <div className="sticky top-[150px]">
                {tocSections.length > 0 && (
                  <div className="rounded-2xl p-8 bg-background" style={{ border: "1px solid hsl(var(--border))" }}>
                    <h4 className="mb-6" style={{ color: "#2A2334" }}>
                      <T page={PAGE} k="tableOfContents" d="Inhoud" />
                    </h4>
                    <ul className="space-y-3">
                      {tocSections.map((section, index) => {
                        const sKey = section.section_key;
                        const headingKey = ak(`sections.${sKey}.heading`);
                        const heading = getTranslatedValue(`sections.${sKey}.heading`, sKey);
                        const isActive = activeSection === sKey;

                        return (
                          <li key={section.id}>
                            <button
                              onClick={() => scrollToSection(sKey)}
                              className={cn(
                                "flex items-start gap-3 w-full text-left transition-all group",
                                isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                              )}
                            >
                              <div className={cn(
                                "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
                                isActive ? "bg-primary" : "bg-[#F3EDFD] group-hover:bg-primary/20"
                              )}>
                                <span className={cn("text-xs font-semibold transition-colors", isActive ? "text-white" : "text-primary")}>
                                  {index + 1}
                                </span>
                              </div>
                              <span className={cn("text-sm transition-colors", isActive && "font-medium")}>
                                {heading || sKey}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                      )}

                {lang === "nl" && (<>
                {/* Shared research report CTA for the research articles */}
                {(article.slug === "onderzoek-leidinggevende-ontwikkelpad" || article.slug === "onderzoek-grootste-mond-verdient-meest") && (
                  <div className="rounded-2xl border-[10px] border-background/70 p-8 mt-6 bg-secondary">
                    <h4 className="font-semibold mb-3 text-foreground">
                      <T page={PAGE} k="employerReport.title" d="Nationaal Werkgeversrapport 2026" />
                    </h4>
                    <Link to={resolveContentLink("/nl/template/nationaal-werkgeversrapport/", lang)}>
                      <Button className="rounded-full mb-4">
                        <T page={PAGE} k="employerReport.download" d="Download" />
                      </Button>
                    </Link>
                    <Link to={resolveContentLink("/nl/template/nationaal-werkgeversrapport/", lang)}>
                      <img
                        src="https://c6b7d37e-c3e6-4f87-a779-7288709ae2ba.lovableproject.com/__l5e/assets-v1/97042264-bae6-400a-a491-0d250abdbb3d/werkgeversrapport-hero-v2.png"
                        alt={t("blog.employerReport.title", { defaultValue: "Nationaal Werkgeversrapport 2026" })}
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}
                {/* Performance Management gids CTA */}
                {(article.slug === "performance-management-systeem" || article.slug === "stappen-bij-aanschaf-nieuwe-performance-management-software" || article.slug === "loonkloof") && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Zo pak je Performance Management slim en eerlijk aan: de gids
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/blog/performance-management-cyclus/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/blog/performance-management-cyclus/`, lang)}>
                      <img
                        src="/images/blog/performance-management-gids-cta.png"
                        alt="Zo pak je Performance Management slim en eerlijk aan: de gids"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for beoordelingssoftware */}
                {article.slug === "beoordelingssoftware" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      E-guide: Breng high- en low-performers in kaart met objectieve evaluaties
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <img
                        src="/images/blog/eguide-low-high-performers.png"
                        alt="E-guide: Breng high- en low-performers in kaart met objectieve evaluaties"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for competentiemanagementsoftware */}
                {article.slug === "competentiemanagementsoftware" && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      E-guide: Breng high- en low-performers in kaart met objectieve evaluaties
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <img
                        src="/images/blog/eguide-low-high-performers.png"
                        alt="E-guide: Breng high- en low-performers in kaart met objectieve evaluaties"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for medewerkerstevredenheidsonderzoek-tool */}
                {article.slug === "medewerkerstevredenheidsonderzoek-tool" && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      MTO vragenlijst: 90 vragen om medewerkerstevredenheid te meten [PDF]
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/learned-engagement-model/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/learned-engagement-model/`, lang)}>
                      <img
                        src="/images/blog/mto-vragenlijst-cta.avif"
                        alt="MTO vragenlijst: 90 vragen om medewerkerstevredenheid te meten"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for evaluatiesoftware */}
                {article.slug === "evaluatiesoftware" && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                      <img
                        src="/images/blog/template-evaluatiegesprek-hero.avif"
                        alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for hr-software */}
                {article.slug === "hr-software" && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Ontwerp een moderne HR-cyclus in 5 heldere stappen
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <img
                        src="/images/blog/hero-19.png"
                        alt="E-guide: Ontwerp een moderne HR-cyclus in 5 stappen"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for performance-management-software */}
                {article.slug === "performance-management-software" && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Zo pak je Performance Management slim en eerlijk aan: de gids
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/blog/performance-management-cyclus/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/blog/performance-management-cyclus/`, lang)}>
                      <img
                        src="/images/blog/performance-management-gids-cta.png"
                        alt="Zo pak je Performance Management slim en eerlijk aan: de gids"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Sidebar download CTA for teamontwikkeling & job-demands-resources-model */}
                {["teamontwikkeling", "team-development", "teamentwicklung", "job-demands-resources-model"].includes(article.slug) && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Ontwerp een moderne HR-cyclus in 5 heldere stappen
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <img
                        src="/images/blog/hero-19.png"
                        alt="E-guide: Ontwerp een moderne HR-cyclus in 5 stappen"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}






                {/* Moderne HR-cyclus e-guide CTA */}
                {(article.slug === "performance-management-cyclus" || article.slug === "moderne-hr-cyclus-tips" || article.slug === "wat-is-het-nieuwe-beoordelen-en-hoe-voer-je-het-in" || article.slug === "hr-trends" || (article.slug === "human-resource-management" && lang === "nl")) && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Ontwerp een moderne HR-cyclus in 5 heldere stappen
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/moderne-hr-cyclus/`, lang)}>
                      <img
                        src="/images/blog/eguide-moderne-hr-cyclus-cta.avif"
                        alt="Ontwerp een moderne HR-cyclus in 5 heldere stappen"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* E-guide: high- en low-performers CTA */}
                {(article.slug === "9-grid-model" || article.slug === "belangrijkste-onderdelen-performance-management-model" || article.slug === "evaluatiegesprek-kwalitatief-zwak" || article.slug === "functioneringsgesprek-voorbereiden" || article.slug === "wat-zijn-okrs-objective-key-results-en-hoe-werk-je-er-mee" || article.slug === "feedback-cultuur" || article.slug === "wees-blij-met-een-excuus-in-het-goede-gesprek" || article.slug === "voorkom-burn-outs-met-vitamine-a-gesprekken" || article.slug === "competenties-inzetten" || article.slug === "belonen-en-het-goede-gesprek" || article.slug === "hoe-kun-je-met-goede-gesprekken-burn-outs-voorkomen" || article.slug === "focus-in-het-goede-gesprek-op-sterke-punten" || article.slug === "waarom-focussen-op-sterke-punten-resulteert-in-beter-presterende-en-lerende-medewerkers" || article.slug === "jobcrafting-ontwikkel-sterke-punten" || article.slug === "hoe-creeer-je-een-veilig-leerklimaat" || article.slug === "stimuleer-medewerkers-ontwikkeling-met-een-growth-mindset" || article.slug === "beoordelingsgesprek-vragen" || article.slug === "learned-haalt-e1-5-miljoen-investering-op-om-skill-based-talent-management-platform-verder-te-ontwikkelen" || article.slug === "planningsgesprek-resultaat" || article.slug === "het-functioneringsgesprek-en-beoordelingsgesprek-combineren" || article.slug === "werken-op-afstand-remote-werken" || article.slug === "beoordelingsgesprek" || article.slug === "vlootschouw" || article.slug === "5-best-practices-van-personeelsontwikkeling" || article.slug === "4g-feedbackmodel" || article.slug === "de-cruciale-rol-van-talent-evaluaties" || article.slug === "6-stappen-om-een-effectief-talentontwikkelingsprogramma-op-te-zetten" || article.slug === "wat-is-topgrading-en-hoe-gaat-het-in-zijn-werk" || article.slug === "5-tips-voor-het-implementeren-van-een-effectieve-successieplanning" || article.slug === "wat-is-upskilling-en-waarom-is-het-essentieel-voor-je-organisatie" || article.slug === "competentiemanagement" || article.slug === "de-ultieme-gids-voor-performance-management" || article.slug === "bedrijfsdoelstellingen-de-voordelen-en-belangrijkste-best-practices-voor-een-effectieve-inrichting" || article.slug === "beoordelen-de-verschillende-toepassingen-en-het-belang-van-objectiviteit" || article.slug === "beoordelingsformulier-de-belangrijkste-tips-voor-het-opstellen-en-toepassen-op-een-rijtje" || article.slug === "de-toegevoegde-waarde-van-het-investeren-in-carriereontwikkeling" || article.slug === "carrierepaden-6-voordelen" || article.slug === "feedforward" || article.slug === "gesprekscyclus" || article.slug === "jaargesprek" || article.slug === "kernwaarden" || (article.slug === "hr-automatisering" && lang === "nl")) && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      E-guide: Breng high- en low-performers in kaart met objectieve evaluaties
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/low-en-high-performers/`, lang)}>
                      <img
                        src="/images/blog/eguide-low-high-performers.png"
                        alt="E-guide: Breng high- en low-performers in kaart met objectieve evaluaties"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Competentieset CTA */}
                {(article.slug === "skill-gap-analyse" || article.slug === "competentiematrix-skills-matrix" || article.slug === "skill-frameworks" || article.slug === "6-tips-voor-het-effectief-gebruiken-van-een-skill-matrix" || article.slug === "competentieprofiel" || article.slug === "functieprofiel" || article.slug === "opstellen-van-functiehuis") && (
                  <div className="rounded-2xl p-6 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <p className="text-sm font-semibold mb-3 text-foreground leading-snug">
                      Competentieset met 38 soft skills [+ voorbeelden per niveau]
                    </p>
                    <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/template/competenties-voorbeelden/`, lang)}>
                      <img
                        src="/images/blog/competentieset-soft-skills-cta.png"
                        alt="Competentieset met 38 soft skills - voorbeelden per niveau"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Skill gap analyse CTA */}
                {article.slug === "6-stappen-voor-het-implementeren-van-een-effectieve-strategische-personeelsplanning" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Skill gap analyse: 4 tips om de skills gap in jouw organisatie te dichten
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/blog/skill-gap-analyse/`, lang)}>
                      <Button className="rounded-full mb-4">Lees meer</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/blog/skill-gap-analyse/`, lang)}>
                      <img
                        src="/images/blog/skill-gap-analyse-hero.png"
                        alt="Skill gap analyse: 4 tips om de skills gap te dichten"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {article.slug === "alles-over-de-wet-loontransparantie-en-de-risicos" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Checklist voor de wet Loontransparantie
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/template/checklist-wet-loontransparantie/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/template/checklist-wet-loontransparantie/`, lang)}>
                      <img
                        src="/images/blog/loontransparantie-checklist-cta.webp"
                        loading="lazy"
                        width={1200}
                        height={627}
                        alt="Checklist voor de wet Loontransparantie"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Loontransparantie scan CTA for 360-graden-feedback-software */}
                {article.slug === "360-graden-feedback-software" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Doe de gratis Loontransparantie scan
                    </h4>
                    <Link to={getPayTransparencyAssessmentPath(lang)}>
                      <Button className="rounded-full mb-4">Start de scan</Button>
                    </Link>
                    <Link to={getPayTransparencyAssessmentPath(lang)}>
                      <img
                        src="/images/blog/loontransparantie-checklist-cta.png"
                        alt="Loontransparantie scan"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}
                {/* Template functioneringsgesprek CTA */}
                {(article.slug === "functioneringsgesprek" || article.slug === "evaluatiegesprek" || article.slug === "10-best-practices-voor-effectieve-loopbaangesprekken" || article.slug === "360-graden-feedback" || article.slug === "personeelsmanagement" || article.slug === "voortgangsgesprek" || article.slug === "start-stop-continue-gesprek" || article.slug === "ontwikkelgesprek-voeren-checklist" || article.slug === "bila-de-best-practices-voor-het-voorbereiden-en-voeren-van-een-effectieve-meeting") && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Template voor een modern functionerings- of evaluatiegesprek
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                      <img
                        src="/images/blog/template-evaluatiegesprek-hero.avif"
                        alt="Template voor een modern functionerings- of evaluatiegesprek"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* E-guide: Betrokkenheid CTA */}
                {(article.slug === "personeelsverloop-verminderen" || article.slug === "onboarding" || article.slug === "mto-anoniem-of-niet-anoniem" || article.slug === "learneds-visie-op-een-modern-medewerkerstevredenheidsonderzoek-mto" || article.slug === "5-redenen-waarom-de-response-rate-van-jouw-mto-tegenvalt-en-hoe-je-deze-kan-verbeteren" || article.slug === "de-5-meest-voorkomende-uitdagingen-bij-het-opstellen-van-een-vragenlijst-voor-een-mto" || article.slug === "voorkom-verloop-met-exit-en-staygesprekken" || article.slug === "waarom-veel-hr-managers-overstappen-op-korte-pulse-surveys" || article.slug === "tips-best-practices-voor-het-uitvoeren-van-een-medewerkersonderzoek" || article.slug === "waarom-organisaties-mto-zelf-uitvoeren" || article.slug === "in-15-seconden-inzicht-in-de-sfeer-in-je-organisatie-met-een-stemmingsmeting" || article.slug === "3-uitdagingen-bij-het-opstellen-van-een-mto-actieplan" || article.slug === "enps-alles-wat-je-moet-weten" || article.slug === "1-op-1-gesprek" || article.slug === "wat-is-quiet-quitting-en-hoe-pak-je-het-effectief-aan" || article.slug === "retentiebeleid" || article.slug === "wat-is-werkgeluk-en-hoe-bevorder-je-dit-in-je-organisatie" || article.slug === "de-effecten-oorzaken-en-beheerstrategieen-van-ziekteverzuim" || article.slug === "learned-engagement-model" || article.slug === "medewerkerstevredenheidsonderzoek-mto-de-ultieme-gids" || article.slug === "employee-value-proposition-evp" || article.slug === "hybride-werken" || article.slug === "bedrijfscultuur-wat-is-het-en-hoe-kun-je-het-verbeteren-binnen-jouw-bedrijf" || article.slug === "binden-en-boeien" || article.slug === "duurzame-inzetbaarheid" || article.slug === "3-tips-om-de-tevredenheid-van-je-medewerkers-te-verhogen" || article.slug === "kpis-gebruiken-voor-het-meten-van-succes-en-impact" || article.slug === "kracht-leercultuur" || article.slug === "het-belang-van-ontwikkeldoelen-binnen-een-organisatie" || article.slug === "het-belang-van-een-effectief-opleidingsplan" || (article.slug === "hr-analytics" && lang === "nl") || (article.slug === "interne-communicatie" && lang === "nl") || (article.slug === "de-4-best-practices-van-people-analytics" && lang === "nl")) && lang === "nl" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      E-guide: Realtime inzicht in medewerkers­betrokkenheid
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/realtime-inzicht-in-betrokkenheid/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/realtime-inzicht-in-betrokkenheid/`, lang)}>
                      <img
                        src="/images/blog/eguide-betrokkenheid-cta.png"
                        alt="E-guide: Realtime inzicht in medewerkersbetrokkenheid"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* MTO-vragenlijst CTA */}
                {(article.slug === "wat-is-medewerkersbetrokkenheid-en-hoe-meet-je-het" || article.slug === "organisatiecultuur" || (article.slug === "hr-manager" && lang === "nl")) && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      MTO vragenlijst: 90 vragen om medewerkerstevredenheid te meten [PDF]
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/learned-engagement-model/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/eguide/learned-engagement-model/`, lang)}>
                      <img
                        src="/images/blog/mto-vragenlijst-cta.avif"
                        alt="MTO vragenlijst: 90 vragen om medewerkerstevredenheid te meten"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {/* Template einde proeftijdgesprek CTA */}
                {article.slug === "4-tips-voor-een-succesvol-proeftijdgesprek" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Template: formulier einde proeftijdgesprek
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/template/einde-proeftijdsgesprek/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                  </div>
                )}

                {/* E-guide objectief PM CTA */}
                {(article.slug === "recruitment" || article.slug === "communicatieplan-voor-betere-werknemersprestaties" || article.slug === "arbeidsvoorwaarden-voorbeelden-en-belangrijke-wetten-bij-het-opstellen" || article.slug === "leren-en-ontwikkelen") && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      E-guide: objectief Performance Management in 5 stappen
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/eguide/objectieve-performance-management/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                  </div>
                )}

                {/* Template planningsgesprek CTA */}
                {(article.slug === "het-planningsgesprek-voorbereiden-tips-en-voorbeelden" || article.slug === "10-best-practices-voor-effectieve-loopbaanontwikkeling") && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Template: planningsgesprek (doelstellingengesprek)
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/template/planningsgesprek-doelstellingengesprek/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <Link to={resolveContentLink(`/${lang}/template/planningsgesprek-doelstellingengesprek/`, lang)}>
                      <img
                        src="/images/blog/planningsgesprek-template-cta.png"
                        alt="Template: planningsgesprek (doelstellingengesprek)"
                        className="w-full h-auto rounded-lg"
                      />
                    </Link>
                  </div>
                )}

                {article.slug === "deloitte-fast-50" && (
                  <div className="rounded-2xl p-8 mt-6 bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      Template voor een modern functionerings- of evaluatiegesprek
                    </h4>
                    <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                      <Button className="rounded-full mb-4">Download</Button>
                    </Link>
                    <img
                      src="/images/blog/template-evaluatiegesprek-cta.avif"
                      alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
      )}



                </>)}
              </div>
            </aside>
          </div>
        </PageContainer>
      </section>

                

      {/* Bottom E-guide CTA for functioneringsgesprek */}
      {lang === "nl" && article.slug === "functioneringsgesprek" && (
        <section className="px-6 py-12">
          <PageContainer>
            <div className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-center bg-secondary" style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}>
              <div className="p-8 md:p-12 flex-1">
                <h3 className="font-semibold mb-2 text-foreground">
                  Gratis e-guide: Zo voer je moderne evaluatiegesprekken
                </h3>
                <p className="text-muted-foreground mb-6">
                  Vernieuw je functioneringsgesprekken met deze 25+ voorbeeldvragen.
                </p>
                <Link to={resolveContentLink(`/${lang}/hr-kenniscentrum/functioneringsgesprek-nieuwe-stijl/`, lang)}>
                  <Button className="rounded-full px-8">Download nu</Button>
                </Link>
              </div>
              <div className="md:w-[45%] p-4 md:p-8">
                <img
                  src="/images/blog/template-evaluatiegesprek-hero.avif"
                  alt="25+ voorbeeldvragen voor modern evaluatiegesprek"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </PageContainer>
        </section>
      )}











      {/* Newsletter Banner */}
      <PageContainer>
        <NewsletterBanner />
      </PageContainer>

      {relatedArticles.length > 0 && (
        <section className="py-16 px-6 bg-gray-50/50">
          <PageContainer>
            <div className="text-center mb-12">
              <h2 className="mb-4" style={{ color: "#2A2334" }}>
                <T page={PAGE} k="relatedArticles" d="Gerelateerde artikelen" />
              </h2>
              <p className="text-lead text-muted-foreground">
                <T page={PAGE} k="discoverMore" d="Ontdek meer over dit onderwerp" />
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle, index) => {
                const cardBackgrounds = ["#F3EDFD", "#FFFBEB", "#FDF2ED"];
                return (
                  <Link
                    key={relatedArticle.id}
                    to={resolveContentLink(`/${lang}/blog/${relatedArticle.category === 'nieuws' ? 'nieuws/' : relatedArticle.category === 'releases' ? 'product-releases/' : ''}${relatedArticle.slug}/`, lang)}
                    className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                    style={{ backgroundColor: cardBackgrounds[index % cardBackgrounds.length], border: "10px solid rgba(255, 255, 255, 0.7)" }}
                  >
                    <div className="h-[160px] overflow-hidden bg-muted">
                      {relatedArticle.hero_image_url ? (
                        <img
                          src={relatedArticle.hero_image_url}
                          alt={t(`${PAGE}.articles.${relatedArticle.slug}.title`) as string}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="h-12 w-12 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 self-start border"
                        style={{ backgroundColor: "#FFFFFF", color: "#9055EE", borderColor: "#9055EE" }}
                      >
                        <T page={PAGE} k={`categories.${relatedArticle.category}`} d={relatedArticle.category} />
                      </span>
                      <h5 className="font-semibold group-hover:text-primary transition-colors" style={{ color: "#2A2334" }}>
                        <T page={PAGE} k={`articles.${relatedArticle.slug}.title`} d={relatedArticle.slug} />
                      </h5>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to={`/${lang}/blog/`}>
                <Button variant="outline" className="rounded-full px-8">
                  <T page={PAGE} k="viewAllArticles" d="Bekijk alle artikelen" />
                </Button>
              </Link>
            </div>
          </PageContainer>
        </section>
      )}
    </article>
  );
};

export default BlogDetail;
